import { Routes } from 'config/routes';
import { UserRole } from 'gql/types';
import { ITabsItem } from 'modules/common/components/CardTabs';
import NavigationCardTabs from 'modules/navigation/components/NavigationCardTabs';
import DonorSettings from 'modules/settings/components/DonorSettings';
import GeneralSettings from 'modules/settings/components/GeneralSettings';
import HandlerDashboardSettings from 'modules/settings/components/HandlerDashboardSettings';
import NightlyInventoryCountSettings from 'modules/settings/components/NightlyInventoryCountSettings';
import NotificationSettings from 'modules/settings/components/NotificationSettings';
import OpsDashboardSettings from 'modules/settings/components/OpsDashboardSettings';
import PhlebotomistDashboardSettings from 'modules/settings/components/PhlebotomistDashboardSettings';
import RecruiterDashboardSettings from 'modules/settings/components/RecruiterDashboardSettings';
import { useCurrentUser } from 'modules/user/hooks/useCurrentUser';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

const SettingsTabs: FC = () => {
  const { t } = useTranslation('settings.SettingsTabs');
  const currentUser = useCurrentUser();
  const role = currentUser.data?.role?.value;
  const isAdmin = role === UserRole.Admin;
  const canSeeNightlyCountSettings =
    isAdmin ||
    role === UserRole.MedicalDirector ||
    role === UserRole.Operations;

  const notificationsTab: ITabsItem = {
    title: 'Notifications',
    content: <NotificationSettings />,
    key: Routes.SettingsNotifications,
  };

  const nightlyCountsTab: ITabsItem = {
    title: 'Nightly Counts',
    content: <NightlyInventoryCountSettings />,
    key: Routes.SettingsNightlyCounts,
  };

  // Non-admin users (MD/Ops): Notifications + Nightly Counts (if allowed)
  if (!isAdmin) {
    const items: ITabsItem[] = [notificationsTab];
    if (canSeeNightlyCountSettings) items.push(nightlyCountsTab);
    return <NavigationCardTabs items={items} />;
  }

  const settingsCardTabs: ITabsItem[] = [
    {
      title: t('general'),
      content: <GeneralSettings />,
      key: Routes.SettingsGeneral,
    },
    {
      title: t('donors'),
      content: <DonorSettings />,
      key: Routes.SettingsDonors,
    },
    {
      title: t('opsDashboard'),
      content: <OpsDashboardSettings />,
      key: Routes.SettingsOpsDashboard,
    },
    {
      title: t('phlebotomistDashboard'),
      content: <PhlebotomistDashboardSettings />,
      key: Routes.SettingsPhlebotomistDashboard,
    },
    {
      title: t('recruiterDashboard'),
      content: <RecruiterDashboardSettings />,
      key: Routes.SettingsRecruiterDashboard,
    },
    {
      title: t('handlerDashboard'),
      content: <HandlerDashboardSettings />,
      key: Routes.SettingsHandlerDashboard,
    },
    nightlyCountsTab,
    notificationsTab,
  ];

  return <NavigationCardTabs items={settingsCardTabs} />;
};

export default SettingsTabs;
